/**
 * useLandRegistrationSearchExtra — resolves the "BKBN finds your property data"
 * upsell for one cart line.
 *
 * Legacy read this from the v1 kind-keyed extras endpoint
 * (`AuthoritiesDocuments.context.ts`: `extraProducts[EXTRA_LAND_REGISTRATION_SEARCH][0]`),
 * which needed nothing but the product to exist. v2 only surfaces extras that
 * Salesforce RELATES to the catalogue product, so this returns null until
 * ISSUE-3329 relates it to the authorities-document products — the card then
 * lights up with no frontend change.
 *
 * Mirrors `useFloorPlanProductFacts`' resolution: shell → variant → extras, found
 * by `productKind` rather than display name.
 */

import { ProductKind } from 'constants/product'
import type { CartItemId, ResolvedExtra } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { useCartLineCatalogueEntry } from '../useCartLineCatalogueEntry'

export interface LandRegistrationSearchExtra {
  /** The catalogue extra, or null when the catalogue offers none for this line. */
  extra: ResolvedExtra | null
  /** Whether the line already carries it. */
  isSelected: boolean
}

export function useLandRegistrationSearchExtra(cartItemId: CartItemId): LandRegistrationSearchExtra {
  const extraServiceIds = usePfStore((s) => s.items.find((i) => i.id === cartItemId)?.extraServiceIds)
  const { variant } = useCartLineCatalogueEntry(cartItemId)
  const extra = variant?.extras.find((e) => e.productKind === ProductKind.EXTRA_LAND_REGISTRATION_SEARCH) ?? null

  return {
    extra,
    isSelected: !!extra && !!extraServiceIds?.includes(extra.id),
  }
}
